import React, { useState, useRef } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Search, AlertCircle, Star, Globe, Mail, CheckCircle, AlertTriangle, XCircle, Zap, ChevronDown, ChevronUp, ArrowRight, Sparkles, X, Upload, Download, FileSpreadsheet, Tag } from 'lucide-react';


const TRIGGER_COLORS = {
    no_website: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
    low_rating: { bg: '#FFF1F2', text: '#BE123C', border: '#FECDD3' },
    low_reviews: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
};

function TriggerBadge({ trigger }) {
    const c = TRIGGER_COLORS[trigger.type] || { bg: '#F0F9FF', text: '#0369A1', border: '#BAE6FD' };
    return (
        <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-none"
            style={{ background: c.bg, color: c.text, borderColor: c.border }}>
            {trigger.label}
        </span>
    );
}

function ScoreBadge({ score }) {
    const color = score >= 70 ? '#00A86B' : score >= 40 ? '#F59E0B' : '#FF3333';
    return (
        <span className="mono text-xs font-bold px-1.5 py-0.5 border" style={{ color, borderColor: color, background: `${color}15` }}>
            {score}
        </span>
    );
}

// ─── AI Query Builder Panel ──────────────────────────────────────────────────
function AIQueryBuilder({ onSelectQuery }) {
    const [open, setOpen] = useState(false);
    const [intent, setIntent] = useState('');
    const [location, setLocation] = useState('');
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [error, setError] = useState('');

    const handleGenerate = async () => {
        if (!intent.trim()) return;
        setLoading(true);
        setError('');
        setSuggestions([]);
        try {
            const { data } = await api.post(
                `/leadgen/ai/query-builder`,
                { intent, location }
            );
            setSuggestions(data.queries || []);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to generate queries. Try again.');
        } finally {
            setLoading(false);
        }
    };

    const CATEGORY_COLORS = [
        '#002FA7', '#6366F1', '#0891B2', '#059669', '#D97706',
        '#BE185D', '#7C3AED', '#DC2626',
    ];

    return (
        <div className="border border-[#002FA7]/30 bg-[#002FA7]/5 mb-4">
            {/* Header toggle */}
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                data-testid="ai-builder-toggle"
            >
                <div className="flex items-center gap-2">
                    <Zap size={12} className="text-[#002FA7]" />
                    <span className="text-xs font-bold uppercase tracking-widest text-[#002FA7]">AI Query Builder</span>
                    <span className="text-[10px] text-[#002FA7]/60 hidden sm:inline">— get smarter search suggestions</span>
                </div>
                {open
                    ? <ChevronUp size={13} className="text-[#002FA7]" />
                    : <ChevronDown size={13} className="text-[#002FA7]" />
                }
            </button>

            {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-[#002FA7]/15">
                    {/* Intent input */}
                    <div className="pt-3">
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1.5">
                            Describe your target in plain English
                        </label>
                        <textarea
                            value={intent}
                            onChange={e => setIntent(e.target.value)}
                            placeholder="e.g. I offer web design to local tradespeople. I want plumbers, electricians and builders who have no website or a very old one."
                            rows={3}
                            className="w-full border border-neutral-300 bg-white px-3 py-2 text-xs rounded-none focus:outline-none focus:border-[#002FA7] resize-none leading-relaxed"
                            data-testid="ai-intent-input"
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1.5">
                            Location (optional)
                        </label>
                        <input
                            type="text"
                            value={location}
                            onChange={e => setLocation(e.target.value)}
                            placeholder="e.g. London, Manchester, Leeds"
                            className="w-full border border-neutral-300 bg-white px-3 py-2 text-xs rounded-none focus:outline-none focus:border-[#002FA7]"
                            data-testid="ai-location-input"
                        />
                    </div>

                    <button
                        onClick={handleGenerate}
                        disabled={loading || !intent.trim()}
                        className="w-full flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold uppercase tracking-wider text-xs py-2.5 transition-colors disabled:opacity-40"
                        data-testid="ai-generate-btn"
                    >
                        <Zap size={11} />
                        {loading ? 'Generating queries...' : 'Generate Search Queries'}
                    </button>

                    {error && (
                        <div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 flex items-start gap-1.5">
                            <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Suggestions */}
                    {suggestions.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500">
                                {suggestions.length} suggested queries — click to use
                            </p>
                            {suggestions.map((s, i) => (
                                <button
                                    key={s.query || i}
                                    onClick={() => { onSelectQuery(s.query); setOpen(false); }}
                                    className="w-full text-left border border-neutral-200 bg-white hover:border-[#002FA7] hover:bg-[#002FA7]/5 px-3 py-2.5 transition-colors group"
                                    data-testid={`suggestion-${i}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span
                                                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 text-white rounded-none flex-shrink-0"
                                                    style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                                                >
                                                    {s.category || 'Query'}
                                                </span>
                                            </div>
                                            <p className="text-xs font-semibold text-neutral-900 group-hover:text-[#002FA7]">
                                                "{s.query}"
                                            </p>
                                            <p className="text-[10px] text-neutral-400 mt-0.5 leading-relaxed">{s.reason}</p>
                                        </div>
                                        <ArrowRight size={12} className="text-neutral-300 group-hover:text-[#002FA7] mt-1 flex-shrink-0 transition-colors" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Import Leads Panel (CSV / XLSX) ────────────────────────────────────────
function ImportLeadsPanel({ onImported }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [locationTag, setLocationTag] = useState('');
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);

    const handleDownloadTemplate = async () => {
        try {
            const { data } = await api.get('/leadgen/leads/import/template', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'leads_import_template.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Failed to download template');
        }
    };

    const handleImport = async () => {
        if (!file) return;
        setImporting(true);
        setError('');
        try {
            const form = new FormData();
            form.append('file', file);
            form.append('location_tag', locationTag);
            const { data } = await api.post('/leadgen/leads/import', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            onImported(data);
            toast.success(`${data.count} lead${data.count === 1 ? '' : 's'} imported${data.duplicates_count ? `, ${data.duplicates_count} duplicate(s) skipped` : ''}`);
            setFile(null);
            if (fileRef.current) fileRef.current.value = '';
            setOpen(false);
        } catch (err) {
            setError(err.response?.data?.detail || 'Import failed. Check the file format and try again.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="border border-emerald-600/30 bg-emerald-600/5">
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                data-testid="import-leads-toggle"
            >
                <div className="flex items-center gap-2">
                    <Upload size={12} className="text-emerald-700" />
                    <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">Import Leads</span>
                    <span className="text-[10px] text-emerald-700/60 hidden sm:inline">— from CSV/XLSX</span>
                </div>
                {open ? <ChevronUp size={13} className="text-emerald-700" /> : <ChevronDown size={13} className="text-emerald-700" />}
            </button>

            {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-emerald-600/15 pt-3">
                    <p className="text-[11px] text-neutral-500 leading-relaxed">
                        Upload a spreadsheet of leads you've researched elsewhere. Company name is required — everything else (contacts, sources, notes) is optional.
                    </p>
                    <button
                        onClick={handleDownloadTemplate}
                        className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:underline"
                        data-testid="download-template-btn"
                    >
                        <Download size={11} /> Download CSV template
                    </button>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1.5">File (.csv or .xlsx)</label>
                        <label
                            className="flex items-center gap-2 border border-dashed border-neutral-300 bg-white px-3 py-3 text-xs cursor-pointer hover:border-emerald-600 transition-colors"
                            data-testid="import-file-drop"
                        >
                            <FileSpreadsheet size={14} className="text-neutral-400 flex-shrink-0" />
                            <span className="truncate text-neutral-600">{file ? file.name : 'Choose a file…'}</span>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={e => setFile(e.target.files?.[0] || null)}
                                className="hidden"
                                data-testid="import-file-input"
                            />
                        </label>
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-widest text-neutral-500 mb-1.5 flex items-center gap-1">
                            <Tag size={10} /> Location tag (optional)
                        </label>
                        <input
                            type="text"
                            value={locationTag}
                            onChange={e => setLocationTag(e.target.value)}
                            placeholder="e.g. Staines"
                            className="w-full border border-neutral-300 bg-white px-3 py-2 text-xs rounded-none focus:outline-none focus:border-emerald-600"
                            data-testid="import-location-tag-input"
                        />
                    </div>

                    {error && (
                        <div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 flex items-start gap-1.5" data-testid="import-error">
                            <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    <button
                        onClick={handleImport}
                        disabled={importing || !file}
                        className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold uppercase tracking-wider text-xs py-2.5 transition-colors disabled:opacity-40"
                        data-testid="run-import-btn"
                    >
                        <Upload size={11} />
                        {importing ? 'Importing…' : 'Import Leads'}
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function LeadDiscovery() {
    const [query, setQuery] = useState('');
    const [limit, setLimit] = useState(20);
    const [includeEmails, setIncludeEmails] = useState(true);
    const [costEstimate, setCostEstimate] = useState(null);
    const [results, setResults] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [runInfo, setRunInfo] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [estimating, setEstimating] = useState(false);

    const handleEstimate = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        setEstimating(true);
        setError('');
        try {
            const { data } = await api.post(`/leadgen/scrape/estimate`, { query, limit, include_emails: includeEmails });
            setCostEstimate(data);
            setShowConfirm(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to get estimate');
        } finally {
            setEstimating(false);
        }
    };

    const handleRun = async () => {
        setShowConfirm(false);
        setLoading(true);
        setError('');
        setResults([]);
        setDuplicates([]);
        try {
            const { data } = await api.post(`/leadgen/scrape/run`, { query, limit, include_emails: includeEmails });
            setResults(data.leads || []);
            setDuplicates(data.duplicates || []);
            setRunInfo({
                count: data.count,
                duplicates_count: data.duplicates_count || 0,
                total_scraped: data.total_scraped || (data.count + (data.duplicates_count || 0)),
                cost: data.actual_cost,
                cost_saved: data.downstream_cost_saved_estimate || 0,
                query: data.query,
                diagnostics: data.diagnostics,
            });
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail && typeof detail === 'object') {
                const lines = [
                    `API Error: ${detail.api_error || 'Unknown error'}`,
                    `SDK: outscraper v${detail.sdk_version} | Class: ${detail.client_class} | Method: ${detail.method}`,
                    detail.request_payload ? `Payload: query="${detail.request_payload.query}", limit=${detail.request_payload.limit}, enrichment=${JSON.stringify(detail.request_payload.enrichment)}` : null,
                ].filter(Boolean).join('\n');
                setError(lines);
            } else {
                setError(typeof detail === 'string' ? detail : 'Failed to run search. Check your Outscraper API key in Settings.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-full" data-testid="discover-page">
            {/* Sidebar */}
            <div className="w-80 flex-shrink-0 border-r border-neutral-200 bg-white overflow-y-auto">
                <div className="p-5 border-b border-neutral-100">
                    <h1 className="text-2xl font-black tracking-tight text-neutral-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        Lead Discovery
                    </h1>
                    <p className="text-xs text-neutral-500 mt-1">Scrape businesses from Google Maps</p>
                </div>

                <div className="p-5 space-y-5">
                    {/* Import from file */}
                    <ImportLeadsPanel onImported={(data) => {
                        setResults(data.leads || []);
                        setDuplicates(data.duplicates || []);
                        setRunInfo({
                            count: data.count,
                            duplicates_count: data.duplicates_count || 0,
                            total_scraped: data.total_rows,
                            query: data.filename,
                            imported: true,
                        });
                    }} />

                    {/* AI Query Builder */}
                    <AIQueryBuilder onSelectQuery={(q) => setQuery(q)} />

                    {/* Search form */}
                    <form onSubmit={handleEstimate} className="space-y-5">
                        {/* Search Query */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-1.5">
                                Search Query *
                            </label>
                            <div className="relative">
                                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="ceramic tile supplier London"
                                    required
                                    className="w-full border border-neutral-300 bg-neutral-50 pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] rounded-none"
                                    data-testid="search-query-input"
                                />
                            </div>
                            <p className="text-[10px] text-neutral-400 mt-1">Use AI builder above for smarter queries</p>
                        </div>

                        {/* Limit */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-1.5">
                                Records Limit: <span className="text-[#002FA7]">{limit}</span>
                            </label>
                            <input
                                type="range" min={5} max={100} step={5}
                                value={limit}
                                onChange={e => setLimit(Number(e.target.value))}
                                className="w-full accent-[#002FA7]"
                                data-testid="limit-slider"
                            />
                            <div className="flex justify-between text-[10px] text-neutral-400 mt-0.5">
                                <span>5</span><span>100</span>
                            </div>
                        </div>

                        {/* Include Emails */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-2">Email Enrichment</label>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <div
                                    onClick={() => setIncludeEmails(p => !p)}
                                    className={`w-10 h-5 relative transition-colors cursor-pointer ${includeEmails ? 'bg-[#002FA7]' : 'bg-neutral-200'}`}
                                    data-testid="include-emails-toggle"
                                >
                                    <div className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${includeEmails ? 'left-5' : 'left-0.5'}`} />
                                </div>
                                <span className="text-xs text-neutral-600">{includeEmails ? 'Fetch emails (recommended)' : 'Skip email enrichment'}</span>
                            </label>
                        </div>

                        {/* Trigger Info */}
                        <div className="border border-neutral-100 bg-neutral-50 p-3 space-y-1.5">
                            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">AI Triggers Detected</p>
                            {[
                                { icon: '🔴', label: 'No Website → Web Design opp.' },
                                { icon: '⭐', label: 'Low Rating → Reputation mgmt.' },
                                { icon: '📉', label: 'Low Reviews → SEO opp.' },
                            ].map(({ icon, label }) => (
                                <div key={label} className="flex items-center gap-2 text-xs text-neutral-500">
                                    <span>{icon}</span><span>{label}</span>
                                </div>
                            ))}
                        </div>

                        <button
                            type="submit"
                            disabled={estimating || !query.trim()}
                            className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold tracking-wide uppercase text-xs py-3 transition-colors disabled:opacity-40"
                            data-testid="estimate-btn"
                        >
                            {estimating ? 'Estimating...' : 'Preview Cost & Run'}
                        </button>
                    </form>

                    {error && (
                        <div className="border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700 flex items-start gap-2" data-testid="discover-error">
                            <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                            <pre className="whitespace-pre-wrap font-mono text-[10px] leading-relaxed">{error}</pre>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Area */}
            <div className="flex-1 overflow-auto">
                {/* Cost Confirm Modal */}
                {showConfirm && costEstimate && (
                    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                        <div className="bg-white border border-neutral-200 w-full max-w-md" data-testid="cost-confirm-modal">
                            <div className="p-6 border-b border-neutral-100">
                                <h2 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Confirm Scrape</h2>
                                <p className="text-xs text-neutral-500 mt-1">Review the cost estimate before proceeding</p>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="bg-neutral-50 border border-neutral-200 p-4 space-y-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-neutral-600">Query</span>
                                        <span className="font-semibold text-neutral-900 text-right max-w-[220px] truncate">"{query}"</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-neutral-600">Records requested</span>
                                        <span className="font-semibold">{costEstimate.records}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-neutral-600">Cost per 1,000</span>
                                        <span className="font-semibold">${costEstimate.cost_per_1000?.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-neutral-200 pt-3 flex justify-between">
                                        <span className="font-semibold uppercase tracking-wider text-xs">Estimated Cost</span>
                                        <span className="text-lg font-black text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                                            ${costEstimate.estimated_cost?.toFixed(4)}
                                        </span>
                                    </div>
                                </div>
                                <p className="text-xs text-neutral-400">{costEstimate.note}</p>

                                {costEstimate.warning && (
                                    <div className="border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2" data-testid="estimate-dup-warning">
                                        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                                        <div className="space-y-1">
                                            <p className="font-semibold">{costEstimate.warning}</p>
                                            {costEstimate.query_history && costEstimate.query_history.length > 0 && (
                                                <ul className="space-y-0.5 mt-1">
                                                    {costEstimate.query_history.slice(0, 3).map((h, i) => (
                                                        <li key={i} className="text-[10px] text-amber-700 mono">
                                                            • {h.created_at?.slice(0, 10)} — {h.new_records ?? '?'} new, {h.duplicates_skipped ?? 0} dupes — ${h.cost?.toFixed?.(4) || h.cost}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            <p className="text-[10px] text-amber-700 mt-1">Outscraper still charges per scrape, but duplicates won't enter your DB and won't be re-verified/emailed.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-6 pt-0 flex gap-3">
                                <button onClick={() => setShowConfirm(false)} className="flex-1 border border-neutral-200 text-neutral-700 font-semibold uppercase tracking-wider text-xs py-2.5 hover:bg-neutral-50 transition-colors" data-testid="cancel-scrape-btn">
                                    Cancel
                                </button>
                                <button onClick={handleRun} className="flex-1 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold uppercase tracking-wider text-xs py-2.5 transition-colors" data-testid="confirm-scrape-btn">
                                    Confirm & Run
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="flex flex-col items-center justify-center h-64 space-y-4">
                        <div className="w-48 h-1 bg-neutral-100 overflow-hidden">
                            <div className="h-full bg-[#002FA7] animate-[slide_1s_linear_infinite]" style={{ width: '50%' }} />
                        </div>
                        <p className="text-sm text-neutral-500 uppercase tracking-widest">Scraping Google Maps...</p>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !results.length && !duplicates.length && !runInfo && (
                    <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
                        <div className="w-16 h-16 bg-[#002FA7] flex items-center justify-center mb-4">
                            <Search size={24} className="text-white" />
                        </div>
                        <h2 className="text-2xl font-black tracking-tight text-neutral-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                            Find Your Next Leads
                        </h2>
                        <p className="text-sm text-neutral-500 max-w-sm mb-2">
                            Use the <strong>AI Query Builder</strong> to describe your target in plain English — it'll suggest the best Google Maps search terms.
                        </p>
                        <p className="text-xs text-neutral-400 max-w-xs">
                            e.g. "I want granite, marble and stone suppliers for kitchen renovations in London"
                        </p>
                    </div>
                )}

                {/* Results */}
                {!loading && (results.length > 0 || duplicates.length > 0) && (
                    <div>
                        {runInfo && (
                            <div className="border-b border-neutral-200 bg-green-50 px-6 py-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className="flex items-center gap-2 text-green-700">
                                            <CheckCircle size={14} />
                                            <span className="font-semibold">{runInfo.count} new lead{runInfo.count === 1 ? '' : 's'} {runInfo.imported ? 'imported' : 'added'}</span>
                                        </span>
                                        {runInfo.duplicates_count > 0 && (
                                            <span className="flex items-center gap-1.5 text-amber-700 border-l border-amber-300 pl-3" data-testid="dupes-summary">
                                                <AlertTriangle size={13} />
                                                <span className="font-semibold">{runInfo.duplicates_count} duplicate{runInfo.duplicates_count === 1 ? '' : 's'} skipped</span>
                                            </span>
                                        )}
                                        <span className="text-neutral-500 text-xs">{runInfo.imported ? 'from' : 'for'} "{runInfo.query}"</span>
                                    </div>
                                    {!runInfo.imported && (
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-green-700 font-semibold mono">Cost: ${runInfo.cost?.toFixed(4)}</span>
                                            {runInfo.cost_saved > 0 && (
                                                <span className="text-xs text-emerald-700 font-semibold mono" title="Estimated Hunter+Resend savings from skipping duplicates">
                                                    Saved: ~${runInfo.cost_saved?.toFixed(4)}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {results.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full table-dense" data-testid="results-table">
                                    <thead>
                                        <tr className="bg-neutral-50">
                                            <th className="text-left">Business</th>
                                            <th className="text-left">Category</th>
                                            <th className="text-center">Rating</th>
                                            <th className="text-left">Email</th>
                                            <th className="text-left">Website</th>
                                            <th className="text-center">Score</th>
                                            <th className="text-left">Triggers</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((lead) => (
                                            <tr key={lead.id} className="hover:bg-neutral-50 transition-colors">
                                                <td>
                                                    <p className="font-semibold text-neutral-900 text-sm">{lead.name}</p>
                                                    <p className="text-xs text-neutral-400 truncate max-w-[200px]">{lead.address}</p>
                                                </td>
                                                <td><span className="text-xs text-neutral-600">{lead.category || '—'}</span></td>
                                                <td className="text-center">
                                                    {lead.rating ? (
                                                        <span className="flex items-center justify-center gap-1 text-xs text-amber-600">
                                                            <Star size={10} fill="currentColor" />{lead.rating}
                                                        </span>
                                                    ) : '—'}
                                                </td>
                                                <td>
                                                    {lead.email
                                                        ? <span className="flex items-center gap-1 text-xs text-neutral-600"><Mail size={10} /><span className="truncate max-w-[150px]">{lead.email}</span></span>
                                                        : <span className="text-neutral-300 text-xs">No email</span>}
                                                </td>
                                                <td>
                                                    {lead.website
                                                        ? <a href={lead.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#002FA7] hover:underline"><Globe size={10} /><span className="truncate max-w-[100px]">Website</span></a>
                                                        : <span className="text-xs text-red-400 font-semibold">None</span>}
                                                </td>
                                                <td className="text-center"><ScoreBadge score={lead.score ?? 0} /></td>
                                                <td>
                                                    <div className="flex flex-wrap gap-1">
                                                        {(lead.triggers || []).map((t) => <TriggerBadge key={t.type} trigger={t} />)}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {duplicates.length > 0 && (
                            <div className="border-t-4 border-amber-200 mt-2" data-testid="duplicates-section">
                                <div className="bg-amber-50/40 px-6 py-3 border-b border-amber-200">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-amber-800 flex items-center gap-2">
                                        <AlertTriangle size={12} />
                                        {duplicates.length} Duplicate{duplicates.length === 1 ? '' : 's'} Skipped — Already in Leads DB
                                    </h3>
                                    <p className="text-[11px] text-amber-700 mt-1">These businesses were detected in your existing database and were not re-inserted. No additional Hunter/Resend credits were used.</p>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full table-dense" data-testid="duplicates-table">
                                        <thead>
                                            <tr className="bg-amber-50/60">
                                                <th className="text-left">Business</th>
                                                <th className="text-left">Category</th>
                                                <th className="text-left">Email</th>
                                                <th className="text-left">Existing Stage</th>
                                                <th className="text-left">Matched On</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {duplicates.map((d, i) => (
                                                <tr key={`${d.existing_lead_id || i}`} className="hover:bg-amber-50/30 transition-colors" data-testid={`duplicate-row-${i}`}>
                                                    <td>
                                                        <p className="font-semibold text-neutral-800 text-sm">{d.name}</p>
                                                        <p className="text-xs text-neutral-400 truncate max-w-[260px]">{d.address}</p>
                                                    </td>
                                                    <td><span className="text-xs text-neutral-600">{d.category || '—'}</span></td>
                                                    <td>
                                                        {d.existing_email
                                                            ? <span className="flex items-center gap-1 text-xs text-neutral-600"><Mail size={10} /><span className="truncate max-w-[150px]">{d.existing_email}</span></span>
                                                            : <span className="text-neutral-300 text-xs">—</span>}
                                                    </td>
                                                    <td>
                                                        <span className="inline-flex text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-neutral-300 bg-white text-neutral-700 rounded-none">
                                                            {(d.existing_stage || 'new_leads').replace(/_/g, ' ')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-[10px] mono text-amber-700 uppercase tracking-wider">{d.matched_on}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
